import { useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { AlertTriangle } from 'lucide-react';
import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import { BOOKING_BOOK_API_ROUTE } from '~/routes/remix-api.booking.book';
import { BookingScheduleControls } from './BookingScheduleControls';
import { BookingScheduleControlsMobile } from './BookingScheduleControlsMobile';
import {
    WORKDAY_END_HOUR,
    WORKDAY_INTERVAL_MINUTES,
    WORKDAY_START_HOUR,
} from '../constants';
import { useBookingSchedule } from '../hooks/use-booking-schedule';
import type { CalendarAvailabilityData } from '../api/get-calendar-availability.server';

export interface BookingPrefill {
    clientName?: string;
    clientEmail?: string;
    clientCompany?: string;
}

export interface ClientDirectorBookingPanelProps {
    /** The Client Director CalendarSession to book against. */
    sessionId: number;
    /**
     * Whether the panel is live. Availability loads when this turns on and
     * fetcher state is reset each time it does, so a panel that stays mounted
     * (e.g. inside a dialog) never shows a stale result on reopen.
     */
    active: boolean;
    /** Seeds the confirmation step, e.g. from an enquiry the user just sent. */
    prefill?: BookingPrefill;
    /**
     * Extra fields posted with the booking, e.g. `enquiryId` so the booking
     * record is linked to the enquiry it completes.
     */
    extraFormFields?: Record<string, string>;
    /** Fires once the booking has been confirmed. */
    onBooked?: (eventId: string | undefined) => void;
    /**
     * Made-up availability for the Business Manager preview. Nothing is loaded
     * and confirming books nothing: it only reports `onBooked`.
     */
    previewAvailability?: CalendarAvailabilityData;
}

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

/**
 * Bookable schedule for a Client Director session, without any surrounding
 * chrome. {@link ClientDirectorBookingModal} wraps it in a dialog; the public
 * enquiry form renders it inline as the step after the details are sent.
 */
export const ClientDirectorBookingPanel = ({
    sessionId,
    active,
    prefill,
    extraFormFields,
    onBooked,
    previewAvailability,
}: ClientDirectorBookingPanelProps) => {
    const { mobile: isMobile } = useAppDisplayModeContext();

    // Bump a "session" counter every time the panel activates so both fetchers
    // below get a fresh key — and therefore fresh state — on each activation.
    // Without this, useFetcher data persists while the panel stays mounted, so
    // a previous availability error/result or a past booking success would
    // carry over and block a retry or show a stale confirmation screen.
    const [openSession, setOpenSession] = useState(0);
    const wasActiveRef = useRef(false);
    useEffect(() => {
        if (active && !wasActiveRef.current) {
            setOpenSession((session) => session + 1);
        }
        wasActiveRef.current = active;
    }, [active]);

    const availabilityFetcher = useFetcher<BookingAvailabilityApiResponse>({
        key: `booking-availability-${sessionId}-${openSession}`,
    });

    useEffect(() => {
        if (!active || previewAvailability) return;
        if (availabilityFetcher.state !== 'idle' || availabilityFetcher.data) {
            return;
        }
        const tz = getBrowserTimezone();
        availabilityFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(tz)}`,
        );
    }, [active, sessionId, availabilityFetcher, previewAvailability]);

    const availability =
        previewAvailability ?? availabilityFetcher.data?.availability;
    const loadError = previewAvailability
        ? undefined
        : availabilityFetcher.data?.error;
    const isLoadingAvailability =
        !previewAvailability &&
        (availabilityFetcher.state !== 'idle' || (!availability && !loadError));

    const booking = useBookingSchedule({
        availability,
        serverTimezone: getBrowserTimezone(),
        actionPath: BOOKING_BOOK_API_ROUTE,
        fetcherKey: `booking-submit-${sessionId}-${openSession}`,
        extraFormFields,
    });

    const notifiedEventRef = useRef<string | null>(null);
    useEffect(() => {
        if (!booking.success) return;
        const key = booking.bookingEventId ?? `session-${openSession}`;
        if (notifiedEventRef.current === key) return;
        notifiedEventRef.current = key;
        onBooked?.(booking.bookingEventId ?? undefined);
    }, [booking.success, booking.bookingEventId, onBooked, openSession]);

    const submitBooking: typeof booking.handleSubmit = previewAvailability
        ? () => onBooked?.(undefined)
        : booking.handleSubmit;

    if (isLoadingAvailability) {
        return (
            <div className="flex min-h-80 w-full items-center justify-center p-10">
                <div className="text-center">
                    <div className="mb-6 inline-block h-16 w-16 animate-spin rounded-full border-4 border-gray-200 border-t-brick-600" />
                    <p className="text-xl font-medium text-gray-900">
                        Preparing your booking experience...
                    </p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="flex min-h-80 w-full items-center justify-center p-10">
                <div className="text-center">
                    <AlertTriangle className="text-rose-600 mx-auto mb-6 h-16 w-16" />
                    <p className="text-base text-gray-700">
                        {loadError.message}
                    </p>
                </div>
            </div>
        );
    }

    if (isMobile) {
        return (
            <BookingScheduleControlsMobile
                sessionTitle={availability?.sessionTitle}
                timezoneName={booking.timezoneName}
                onTimezoneChange={booking.handleTimezoneChange}
                availableTimezones={booking.availableTimezones}
                meetingType={booking.meetingType}
                consultedMember={booking.consultedMember}
                sessionImageUrl={booking.sessionImageUrl}
                professional={booking.professional}
                isProfessionalLoading={false}
                selectedDateTimestamp={booking.selectedDateTimestamp}
                onCalendarSelect={booking.handleCalendarSelect}
                freeDays={booking.freeDays}
                durations={booking.durations}
                selectedDuration={booking.selectedDuration}
                onDurationChange={booking.handleDurationChange}
                isDurationDisabled={booking.isDurationDisabled}
                freeHours={booking.freeHoursForSelectedDay}
                selectedSlotTimestamp={booking.selectedSlotTimestamp}
                onTimeSelect={booking.handleTimeSelectWithAssignment}
                normalizedSelectedSlotTimestamp={booking.selectedSlotTimestamp}
                selectedSlotLabel={booking.selectedSlotLabel}
                statusMessage={booking.statusMessage}
                success={booking.success}
                failure={booking.failure}
                onSubmit={({ userName, userEmail, userCompany }) => {
                    submitBooking({
                        clientName: userName,
                        clientEmail: userEmail,
                        clientCompany: userCompany,
                    });
                }}
                bookingDisabled={booking.bookingDisabled}
                confirmButtonLabel="Confirm"
                userEmailLabel="Enter your email address"
                submissionState={booking.submissionState}
                timePickerStartHour={WORKDAY_START_HOUR}
                timePickerEndHour={WORKDAY_END_HOUR}
                timePickerStepMinutes={WORKDAY_INTERVAL_MINUTES}
                eventId={booking.bookingEventId}
                showSpamHint={booking.showSpamHint}
                initialBookingDetails={prefill}
            />
        );
    }

    return (
        <BookingScheduleControls
            timezoneName={booking.timezoneName}
            onTimezoneChange={booking.handleTimezoneChange}
            availableTimezones={booking.availableTimezones}
            meetingType={booking.meetingType}
            consultedMember={booking.consultedMember}
            sessionImageUrl={booking.sessionImageUrl}
            professional={booking.professional}
            isProfessionalLoading={false}
            selectedDateTimestamp={booking.selectedDateTimestamp}
            onCalendarSelect={booking.handleCalendarSelect}
            freeDays={booking.freeDays}
            durations={booking.durations}
            selectedDuration={booking.selectedDuration}
            onDurationChange={booking.setSelectedDuration}
            isDurationDisabled={booking.isDurationDisabled}
            freeHours={booking.freeHoursForSelectedDay}
            selectedSlotTimestamp={booking.selectedSlotTimestamp}
            onTimeSelect={booking.handleTimeSelectWithAssignment}
            normalizedSelectedSlotTimestamp={booking.selectedSlotTimestamp}
            selectedSlotLabel={booking.selectedSlotLabel}
            statusMessage={booking.statusMessage}
            success={booking.success}
            failure={booking.failure}
            onSubmit={submitBooking}
            bookingDisabled={booking.bookingDisabled}
            confirmButtonLabel="Confirm"
            userEmailLabel="Enter your email address"
            submissionState={booking.submissionState}
            timePickerVariant="list"
            timePickerStartHour={WORKDAY_START_HOUR}
            timePickerEndHour={WORKDAY_END_HOUR}
            timePickerStepMinutes={WORKDAY_INTERVAL_MINUTES}
            onConfirmationPhaseChange={booking.setIsConfirmationPhase}
            eventId={booking.bookingEventId}
            showSpamHint={booking.showSpamHint}
            initialBookingDetails={prefill}
        />
    );
};
